import { DataGridController } from '@/components/dataGridController/dataGridController';
import Status from '@/modules/beneficiary/components/status/Status';
import {
  BENEFICIARY_EXTRA_FILTERS,
  BENEFICIARY_STATUS_ALIAS_TRANSLATED,
  OWNERSHIP_TRANSLATED,
} from '@/modules/beneficiary/entities/constants';
import {
  BeneficiaryNormalized,
  BeneficiaryResponseNormalized,
  BeneficiarySearchParams,
} from '@/modules/beneficiary/entities/interfaces';
import BeneficiaryService from '@/modules/beneficiary/services/beneficiaryService';
import { SelectNegotiationsByBenefit, useNegotiations } from '@/modules/negotiation';
import { isEmpty } from 'lodash';
import React, { Dispatch, SetStateAction, useCallback } from 'react';

export enum BeneficiariesTableColumns {
  NAME = 'name',
  CPF = 'cpf',
  STATUS = 'status',
  HEALTH_CARD = 'healthCard',
  BENEFIT = 'benefit',
}

export enum BeneficiariesTableFilters {
  NAME = 'name',
  CPF = 'cpf',
  STATUS = 'status',
  TYPE = 'type',
  HEALTH_CARD = 'healthCard',
  BENEFIT = 'benefit',
}

export enum BeneficiariesTableActions {
  TO_GO_DETAILS = 'goToDetails',
}

export interface BeneficiariesTableFiltersParams extends BeneficiarySearchParams {
  showMoreFilters?: boolean;
}

export interface BeneficiariesTableProps {
  visibleColumns?: BeneficiariesTableColumns[];
  visibleFilters?: BeneficiariesTableFilters[];
  visibleActions?: BeneficiariesTableActions[];
  filters: BeneficiariesTableFiltersParams;
  setFilters: Dispatch<SetStateAction<BeneficiariesTableFiltersParams>>;
}

export const BeneficiariesTable: React.FC<BeneficiariesTableProps> = ({
  visibleColumns,
  visibleFilters,
  visibleActions,
  filters,
  setFilters,
}) => {
  const { negotiationsById } = useNegotiations({
    withDeleted: true,
  });

  const getBeneficiaries = useCallback(
    async (filters: BeneficiarySearchParams, companyId: string) => {
      const { data, ...pagination } =
        await new BeneficiaryService().getBeneficiaries<BeneficiaryResponseNormalized>({
          ...filters,
          companyId,
        });

      return {
        ...pagination,
        data: data.map((beneficiary) => {
          return {
            ...beneficiary,
            negotiation: negotiationsById[beneficiary.negotiationId],
          };
        }),
      };
    },
    [negotiationsById],
  );

  return (
    <DataGridController
      key={String(isEmpty(negotiationsById))}
      filterStateController={[filters, setFilters]}
      source={getBeneficiaries}
      visibleColumns={visibleColumns}
      columnByName={{
        name: {
          headerName: 'Nome',
          field: 'life.name',
          sup: {
            field: 'ownership',
            textParams: ({ isHolder }) => ({
              color: isHolder ? 'success' : 'colorDefault',
            }),
          },
        },
        cpf: {
          headerName: 'CPF',
          field: 'life.cpf',
        },
        healthCard: {
          headerName: 'Carteirinha',
          field: 'healthCardNumber',
        },
        benefit: {
          headerName: 'Benefício',
          field: 'negotiation.product.benefit.name',
        },
        status: {
          headerName: 'Status',
          render: ({ status }: BeneficiaryNormalized) => <Status label={status} />,
        },
      }}
      visibleFilters={visibleFilters}
      filterByName={{
        name: {
          label: 'Nome',
          key: 'name',
        },
        cpf: {
          label: 'CPF',
          key: 'cpf',
        },
        status: {
          label: 'Status',
          key: 'status',
          type: 'select',
          data: Object.entries(BENEFICIARY_STATUS_ALIAS_TRANSLATED).map(([key, val]) => ({
            text: `${val}s`,
            value: key,
          })),
        },
        type: {
          label: 'Tipo',
          key: 'type',
          type: 'select',
          extra: true,
          data: Object.entries(OWNERSHIP_TRANSLATED).map(([key, val]) => ({
            text: val,
            value: key,
          })),
        },
        healthCard: {
          label: 'Carteirinha',
          key: 'healthCard',
          extra: true,
        },
        benefit: {
          extra: true,
          render: ({ benefit }, onChange) => (
            <SelectNegotiationsByBenefit
              key={benefit || BENEFICIARY_EXTRA_FILTERS.BENEFIT}
              value={benefit}
              onChangeSelected={(benefit, negotiationId) => onChange({ benefit, negotiationId })}
            />
          ),
        },
      }}
      visibleActions={visibleActions}
      actionByName={{
        goToDetails: {
          redirectToDetail: ({ id }, companyId) =>
            `/companies/${companyId}/beneficiaries/${id}/details`,
        },
      }}
    />
  );
};
